/* eslint-disable no-param-reassign */
import isEmptyObj from 'lodash/isEmpty';
import validator from 'validator';
import { isValidNumber } from 'libphonenumber-js';
import moment from 'logic/moment';
import { isValidIBAN } from 'ibantools';

const isEmpty = (str) => {
  if (!str) {
    return true;
  } if (validator.isEmpty(str.trim())) {
    return true;
  }
  return false;
};

const validateUser = (values, errors, props) => {
  if (isEmpty(values.username)) {
    errors.username = props.intl
      .formatMessage({ id: 'userForm.requiredEmail', defaultMessage: 'Email required' });
  } else if (!validator.isEmail(values.username)) {
    errors.username = props.intl
      .formatMessage({ id: 'userForm.invalidEmail', defaultMessage: 'Invalid email' });
  }
  if (props && values.username !== props.username) {
    if (values.username_confirm !== values.username) {
      errors.username_confirm = props.intl.formatMessage({
        id: 'userForm.emaildMismatch',
        defaultMessage: 'Email doesn\'t match.',
      });
    }
  }

  if (isEmpty(values.phone_1)) {
    errors.phone_1 = props.intl.formatMessage({
      id: 'userForm.requiredPhone',
      defaultMessage: 'Phone (primary) is required',
    });
  } else if (!isValidNumber(values.phone_1) || !/^\+(?:[0-9] ?){6,14}[0-9]$/.test(values.phone_1)) {
    errors.phone_1 = props.intl.formatMessage({
      id: 'Invalid phone number',
      defaultMessage: 'Invalid phone number',
    });
  }

  if (values.phone_2 && (!isValidNumber(values.phone_2) || !/^\+(?:[0-9] ?){6,14}[0-9]$/.test(values.phone_2))) {
    errors.phone_2 = props.intl.formatMessage({
      id: 'Invalid phone number',
      defaultMessage: 'Invalid phone number',
    });
  }

  if (isEmpty(values.first_name)) {
    errors.first_name = props.intl.formatMessage({
      id: 'userForm.requiredFirstName',
      defaultMessage: 'First name is required',
    });
  }

  if (isEmpty(values.last_name)) {
    errors.last_name = props.intl.formatMessage({
      id: 'userForm.requiredLastName',
      defaultMessage: 'Last name is required',
    });
  }

  if (!values.birthday) {
    errors.birthday = props.intl
      .formatMessage({ id: 'userForm.requiredBirthday', defaultMessage: 'Birthday is required' });
  } else if (!!values.birthday) {
    const birthday = moment(values.birthday);
    if (!birthday.isValid()) {
      errors.birthday = props.intl
        .formatMessage({ id: 'userForm.invalidDate', defaultMessage: 'Invalid date' });
    } else {
      const limit18 = moment().subtract(18, 'years');
      const limit100 = moment().subtract(100, 'years');
      if (!limit18.isAfter(birthday)) {
        errors.birthday = props.intl
          .formatMessage({ id: 'userForm.lessAge', defaultMessage: 'Age should be above 18' });
      } else if (limit100.isAfter(birthday)) {
        errors.birthday = props.intl
          .formatMessage({ id: 'userForm.invalidAge', defaultMessage: 'Invalid age' });
      }
    }
  }

  if (isEmpty(values.nationality)) {
    errors.nationality = props.intl
      .formatMessage({ id: 'userForm.requiredNation', defaultMessage: 'Nationality is required' });
  }

  if (isEmpty(values.country_of_residence)) {
    errors.country_of_residence = props.intl
      .formatMessage({ id: 'userForm.requiredCountry', defaultMessage: 'Country is required' });
  }
};

const validatePassword = (values, errors, props) => {
  if (props.type === 'SOFT') {
    if (isEmpty(values.password)) {
      errors.password = props.intl
        .formatMessage({ id: 'userForm.requiredPassword', defaultMessage: 'Password is required' });
    }

    if (values.password && values.password.length < 6) {
      errors.password = props.intl.formatMessage({
        id: 'userForm.min6CharsPassword',
        defaultMessage: 'Password should have at least 6 characters',
      });
    }

    if (values.password && values.password !== values.confirm_password) {
      errors.confirm_password = props.intl.formatMessage({
        id: 'userForm.passwordMismatch',
        defaultMessage: 'Password doesn\'t match',
      });
    }
  }
};

const validateCompany = (values, errors, props) => {
  if (!values.isCompany) {
    return;
  }

  if (isEmpty(values.business_type)) {
    errors.business_type = props.intl.formatMessage({
      id: 'userForm.requiredCompanyType',
      defaultMessage: 'Company type required',
    });
  }

  if (isEmpty(values.business_name)) {
    errors.business_name = props.intl.formatMessage({
      id: 'userForm.requiredCompanyName',
      defaultMessage: 'Company name required',
    });
  }
};

export const validateUserForm = (values, props) => {
  const errors = {};
  validateUser(values, errors, props);
  validatePassword(values, errors, props);
  validateCompany(values, errors, props);

  return errors;
};

export const validateNewUserFormPatch = (props) => (values) => {
  return validateUserForm(values, props);
};

export const validateUploadID = (values, props) => {
  const errors = {};

  if (isEmptyObj(errors) && !values.termsOfPayment) {
    errors.termsOfPayment = props.intl.formatMessage({
      id: 'userForm.checkboxError',
      defaultMessage: 'Please check the checkbox',
    });
  }

  if (isEmpty(values.id_last_name)) {
    errors.id_last_name = props.intl.formatMessage({
      id: 'userForm.requiredLastName',
      defaultMessage: 'Last name is required',
    });
  }

  if (isEmpty(values.id_first_name)) {
    errors.id_first_name = props.intl.formatMessage({
      id: 'userForm.requiredFirstName',
      defaultMessage: 'First name is required',
    });
  }

  if (isEmpty(values.first_name)) {
    errors.first_name = props.intl.formatMessage({
      id: 'userForm.requiredFirstName',
      defaultMessage: 'First name is required',
    });
  } else if (!isEmpty(values.id_first_name)) {
    const mangoFirst = values.id_first_name
      .trim()
      .split(' ')
      .map((s) => s.toLowerCase().trim());
    const firstName = values.first_name
      .trim()
      .split(' ')
      .map((s) => s.toLowerCase().trim());

    if (!firstName.every((n) => mangoFirst.includes(n))) {
      errors.first_name = props.intl.formatMessage({
        id: 'userForm.firstNameNotInID',
        defaultMessage: 'Choose at least one of {names}',
      }, { names: mangoFirst.join(', ') });
    }
  }

  return errors;
};

export const validateNewUploadIDPatch = (props) => (values) => {
  return validateUploadID(values, props);
};

export const validateBankAccount = (values, props) => {
  const errors = {};

  if (isEmptyObj(errors) && !values.termsOfPayment) {
    errors.termsOfPayment = props.intl.formatMessage({
      id: 'userForm.checkboxError',
      defaultMessage: 'Please check the checkbox',
    });
  }

  if (!isValidIBAN(values.iban)) {
    errors.iban = props.intl.formatMessage({
      id: 'userForm.ibanNotValid',
      defaultMessage: 'IBAN is not valid',
    });
  }

  if (!values.owner) {
    errors.owner = props.intl.formatMessage({
      id: 'userForm.requiredOwner',
      defaultMessage: 'Owner name is required',
    });
  }

  if (!values.country_of_residence) {
    errors.country_of_residence = props.intl.formatMessage({
      id: 'userForm.countryRequired',
      defaultMessage: 'Country of residence is required',
    });
  }

  if (!values.address_line_1) {
    errors.address_line_1 = props.intl.formatMessage({
      id: 'userForm.addressBank1Required',
      defaultMessage: 'Address is required',
    });
  }

  if (!values.postal_code) {
    errors.postal_code = props.intl.formatMessage({
      id: 'userForm.requiredPostalCode',
      defaultMessage: 'Postal code is required',
    });
  }

  if (!values.city) {
    errors.city = props.intl.formatMessage({
      id: 'userForm.requiredCity',
      defaultMessage: 'City is required',
    });
  }

  return errors;
};

export const validateBankAccountPatch = (props) => (values) => {
  return validateBankAccount(values, props);
};

export const trimString = (value) => (typeof value === 'string' ? value.trim() : value);
export const trimData = (values) => {
  const data = {};
  Object.keys(values).forEach((key) => {
    data[key] = trimString(values[key]);
  });

  return data;
};

export function cleanUpData(data) {
  data = trimData(data);
  // There is mismatch between how data represented in redux-form and backend. We address that here.
  const cleanData = {
    birthday: moment(data.birthday).format('YYYY-MM-DD'),
    country_of_residence: data.country_of_residence,
    first_name: data.first_name,
    hide_phone_number: !data.show_phone_number,
    hide_last_name: data.hide_last_name,
    language: data.language,
    last_name: data.last_name,
    nationality: data.nationality,
    type_of_person: data.isCompany ? 'LEGAL' : 'NATURAL',
    business_type: data.isCompany ? data.business_type : null,
    business_name: data.isCompany ? data.business_name : null,
    username: data.username,
  };

  // For the below fields, return null if data is not defined
  ['phone_1', 'phone_2'].forEach((field) => {
    if (data[field] === '') {
      cleanData[field] = null;
    } else {
      cleanData[field] = data[field];
    }
  });
  // Skip below fields, if data is not defined
  if (data.password && data.password.trim() !== '') {
    cleanData.password = data.password;
  }

  return cleanData;
}

export const validateNaturalHostInformation = (props, legalTin = false, validateIban = false, validateTerms = false) => (values) => {
  const errors = {};
  values = trimData(values);
  [
    ['tin_number', 10, 80],
    ['u_address1', 1, 40],
    ['u_postal_code', 1, 8],
    ['u_city', 1, 40],
    ['u_country', 2, 2],
    ['nationality', 2, 2],
    ['country_of_residence', 2, 2],
    ['first_name', 1, 255],
    ['last_name', 1, 255],
  ].forEach(([field, min, max]) => {
    if (!values[field]) {
      errors[field] = props.intl.formatMessage({
        id: 'Required', defaultMessage: 'Required',
      });
    } else {
      if (values[field]?.length > max) {
        errors[field] = props.intl.formatMessage({
          id: 'validation.max_chars',
          defaultMessage: 'Max number of chars: {max}',
        }, { max });
      }

      if (values[field]?.length < min) {
        errors[field] = props.intl.formatMessage({
          id: 'validation.min_chars',
          defaultMessage: 'Min number of chars: {min}',
        }, { min });
      }
    }
  });

  if (!legalTin && values.nationality === 'SE' && !/^[0-9]{10}$/.test(values.tin_number)) {
    errors.tin_number = props.intl.formatMessage({
      id: 'label.pnr.format', defaultMessage: 'YYMMDDNNNN (10 number format)',
    });
  }

  if (validateIban) {
    if (!values.iban) {
      errors.iban = props.intl.formatMessage({
        id: 'Required', defaultMessage: 'Required',
      });
    }

    if (!values.owner) {
      errors.owner = props.intl.formatMessage({
        id: 'Required', defaultMessage: 'Required',
      });
    }
  }

  if (values.iban) {
    if (!isValidIBAN(values.iban)) {
      errors.iban = props.intl.formatMessage({
        id: 'userForm.ibanNotValid',
        defaultMessage: 'IBAN is not valid!',
      });
    }
  }

  if (['CA', 'US', 'MX'].includes(values.u_country)) {
    if (!values.u_region) {
      errors.u_region = props.intl.formatMessage({
        id: 'Required', defaultMessage: 'Required',
      });
    }
  }

  if (validateTerms && !values.termsOfPayment) {
    errors.termsOfPayment = props.intl.formatMessage({
      id: 'Required', defaultMessage: 'Required',
    });
  }

  return errors;
};

export const validateLegalHostInformation = (props, validateIban = false, validateTerms = false) => (values) => {
  const errors = validateNaturalHostInformation(props, true, validateIban)(values);

  [
    ['hq_address1', 1, 40],
    ['hq_postal_code', 1, 8],
    ['hq_city', 1, 40],
    ['hq_country', 2, 2],
    ['business_type', 1, 20],
    ['business_name', 1, 255],
    ['company_number', 1, 20],
  ].forEach(([field, min, max]) => {
    if (!values[field]) {
      errors[field] = props.intl.formatMessage({
        id: 'Required', defaultMessage: 'Required',
      });
    } else {
      if (values[field]?.length > max) {
        errors[field] = props.intl.formatMessage({
          id: 'validation.max_chars',
          defaultMessage: 'Max number of chars: {max}',
        }, { max });
      }

      if (values.hq_country === 'SE' && !/^[0-9]{6}[-][0-9]{4}$/.test(values.company_number)) {
        errors.company_number = props.intl.formatMessage({
          id: 'label.orgnr.format', defaultMessage: 'Input format XXXXXX-YYYY',
        });
      }

      if (values[field]?.length < min) {
        errors[field] = props.intl.formatMessage({
          id: 'validation.min_chars',
          defaultMessage: 'Min number of chars: {min}',
        }, { min });
      }
    }
  });

  if (['CA', 'US', 'MX'].includes(values.hq_country)) {
    if (!values.hq_region) {
      errors.hq_region = props.intl.formatMessage({
        id: 'Required', defaultMessage: 'Required',
      });
    }
  }

  if (values.vat_registered !== 'NO') {
    values.vat_number = values.vat_number.trim();
    if (!values.vat_number) {
      errors.vat_number = props.intl.formatMessage({
        id: 'Required', defaultMessage: 'Required',
      });
    } else if (values.vat_number?.length > 50 || values.vat_number?.length < 5) {
      errors.vat_number = props.intl.formatMessage({
        id: 'vat.number.format',
        defaultMessage: 'Bad VAT format',
      });
    }

    if (!values.vat_rate) {
      errors.vat_rate = props.intl.formatMessage({
        id: 'Required', defaultMessage: 'Required',
      });
    } else if (!String(values.vat_rate).match(/^[0-9]+\.?[0-9]?$/)) {
      errors.vat_rate = props.intl.formatMessage({
        id: 'vat.rate.format', defaultMessage: 'Number format error',
      });
    } else if (values.vat_rate > 100 || values.vat_rate < 0) {
      errors.vat_rate = props.intl.formatMessage({
        id: 'vat.rate.help',
        defaultMessage: 'Tax rate in percent (0-100)',
      });
    }
  }

  if (!!values.birthday) {
    const birthday = moment(values.birthday);
    if (!birthday.isValid()) {
      errors.birthday = props.intl
        .formatMessage({ id: 'userForm.invalidDate', defaultMessage: 'Invalid date!' });
    } else {
      const limit18 = moment().subtract(18, 'years');
      const limit100 = moment().subtract(100, 'years');
      if (!limit18.isAfter(birthday)) {
        errors.birthday = props.intl
          .formatMessage({ id: 'userForm.lessAge', defaultMessage: 'Age should be above 18' });
      } else if (limit100.isAfter(birthday)) {
        errors.birthday = props.intl
          .formatMessage({ id: 'userForm.invalidAge', defaultMessage: 'Invalid Age' });
      }
    }
  }

  if (validateTerms && !values.termsOfPayment) {
    errors.termsOfPayment = props.intl.formatMessage({
      id: 'Required', defaultMessage: 'Required',
    });
  }

  return errors;
};

export const cleanHostInformationRequestData = (data) => {
  data = trimData(data);
  const allowed = [
    'tin_number',
    'u_address1',
    'u_address2',
    'u_postal_code',
    'u_city',
    'u_country',
    'u_region',
    'first_name',
    'last_name',
    'iban',
    'nationality',
    'country_of_residence',
    'owner',
    'hq_address1',
    'hq_address2',
    'hq_postal_code',
    'hq_city',
    'hq_country',
    'hq_region',
    'company_number',
    'vat_registered',
    'vat_number',
    'business_type',
    'business_name',
  ];

  return allowed.reduce((acc, key) => {
    return {
      ...acc,
      [key]: data[key] || null,
    };
  }, {
    vat_rate: data.vat_rate || 0,
    birthday: moment(data.birthday).format('YYYY-MM-DD'),
  });
};
